"use client";

import { useState } from "react";
import Link from "next/link";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Loader2 } from "lucide-react";
import { IconBrandGithub, IconBrandGoogle } from "@tabler/icons-react";
import { toast } from "sonner";
import { Button } from "@ocr-monorepo/ui/components/button";
import { Checkbox } from "@ocr-monorepo/ui/components/checkbox";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@ocr-monorepo/ui/components/form";
import { Input } from "@ocr-monorepo/ui/components/input";
import { signIn, signUp } from "@/lib/auth-client";
import { env } from "@/env";
import {
  captureGrowthEvent,
  consumeSignupMethod,
  markSignupStarted,
} from "@/lib/analytics";

interface SignUpStepProps {
  onSignUp: () => void;
}

// Better Auth's configured minimum is eight characters.
const signUpSchema = z.object({
  email: z.string().trim().pipe(z.email("Please enter a valid email address")),
  password: z.string().min(8, "Use at least 8 characters"),
  acceptTerms: z.boolean().refine(Boolean, {
    message: "Please accept the terms and privacy policy",
  }),
});

type SignUpValues = z.infer<typeof signUpSchema>;

export function SignUpStep({ onSignUp }: SignUpStepProps) {
  const [isSigningUp, setIsSigningUp] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const form = useForm<SignUpValues>({
    defaultValues: { email: "", password: "", acceptTerms: false },
    resolver: zodResolver(signUpSchema),
  });

  const syncAutofilledCredentials = (element: HTMLFormElement) => {
    // Safari/password managers can fill the DOM without firing React's onChange.
    // Read both fields before a render or validation can replace those values.
    const values = new FormData(element);
    form.setValue("email", String(values.get("email") ?? ""));
    form.setValue("password", String(values.get("password") ?? ""));
  };

  const onSubmit = async (data: SignUpValues) => {
    setIsSigningUp(true);
    markSignupStarted("email");
    captureGrowthEvent("signup_started", { method: "email" });

    try {
      const { error } = await signUp.email({
        name: data.email,
        email: data.email,
        password: data.password,
        callbackURL: `${env.NEXT_PUBLIC_BASE_URL}/getting-started`,
      });

      if (error) {
        captureGrowthEvent("signup_failed", {
          method: "email",
          reason: error.code ?? "auth_error",
        });
        if (error.code === "USER_ALREADY_EXISTS_USE_ANOTHER_EMAIL") {
          form.setError("email", {
            message: "This email already has an account. Sign in instead.",
          });
        } else {
          toast.error("We couldn't create your account. Please try again.");
        }
        return;
      }

      captureGrowthEvent("signup_succeeded", { method: "email" });
      captureGrowthEvent("onboarding_step_completed", {
        step: "create_account",
        method: "email",
      });
      onSignUp();
    } catch {
      captureGrowthEvent("signup_failed", { method: "email", reason: "network" });
      toast.error("We couldn't connect. Please try again.");
    } finally {
      consumeSignupMethod();
      setIsSigningUp(false);
    }
  };

  const handleSocialSignup = async (method: "google" | "github") => {
    setIsSigningUp(true);
    markSignupStarted(method);
    captureGrowthEvent("signup_started", { method });

    try {
      const { error } = await signIn.social({
        provider: method,
        callbackURL: `${env.NEXT_PUBLIC_BASE_URL}/getting-started`,
      });
      if (error) {
        consumeSignupMethod();
        captureGrowthEvent("signup_failed", {
          method,
          reason: error.code ?? "auth_error",
        });
        toast.error("We couldn't connect to that provider. Please try again.");
      }
    } catch {
      consumeSignupMethod();
      captureGrowthEvent("signup_failed", { method, reason: "network" });
      toast.error("We couldn't connect. Please try again.");
    } finally {
      setIsSigningUp(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-[640px]">
      <div className="mb-4 space-y-2 md:mb-5 md:text-center">
        <p className="text-[13px] leading-5 text-muted-foreground md:hidden">
          Step 1 of 3 · Account
        </p>
        <h1 className="text-[28px] font-medium leading-[34px] tracking-tight md:text-[30px] md:leading-9">
          Create your account
        </h1>
        <p className="text-[15px] leading-6 text-muted-foreground">
          Start with 20 pages. No card required.
        </p>
      </div>

      <Form {...form}>
        <form
          noValidate
          onSubmit={(event) => {
            syncAutofilledCredentials(event.currentTarget);
            void form.handleSubmit(onSubmit, (errors) => {
              captureGrowthEvent("signup_validation_failed", {
                fields: Object.keys(errors),
              });
            })(event);
          }}
          className="space-y-3.5"
        >
          <div className="space-y-3">
            <Button
              type="button"
              variant="outline"
              className="h-12 w-full rounded-md bg-card text-sm"
              disabled={isSigningUp}
              onClick={() => void handleSocialSignup("google")}
            >
              <IconBrandGoogle className="size-4" aria-hidden="true" />
              Continue with Google
            </Button>
            <Button
              type="button"
              variant="outline"
              className="h-12 w-full rounded-md bg-card text-sm"
              disabled={isSigningUp}
              onClick={() => void handleSocialSignup("github")}
            >
              <IconBrandGithub className="size-4" aria-hidden="true" />
              Continue with GitHub
            </Button>
          </div>

          <div className="flex items-center gap-3 text-[13px] text-muted-foreground">
            <span className="h-px flex-1 bg-border" aria-hidden="true" />
            <span>or use your email</span>
            <span className="h-px flex-1 bg-border" aria-hidden="true" />
          </div>

          <FormField
            name="email"
            control={form.control}
            render={({ field }) => (
              <FormItem className="gap-1.5">
                <FormLabel className="text-sm font-medium">Email</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    placeholder="you@example.com"
                    disabled={isSigningUp}
                    className="h-12 rounded-md bg-card px-3.5 text-base md:text-base"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            name="password"
            control={form.control}
            render={({ field }) => (
              <FormItem className="gap-1.5">
                <FormLabel className="text-sm font-medium">Password</FormLabel>
                <div className="relative">
                  <FormControl>
                    <Input
                      {...field}
                      type={showPassword ? "text" : "password"}
                      autoComplete="new-password"
                      placeholder="Create a password"
                      disabled={isSigningUp}
                      className="h-12 rounded-md bg-card px-3.5 pr-16 text-base md:text-base"
                    />
                  </FormControl>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={isSigningUp}
                    onClick={(event) => {
                      if (event.currentTarget.form) {
                        syncAutofilledCredentials(event.currentTarget.form);
                      }
                      setShowPassword((value) => !value);
                    }}
                    className="absolute inset-y-0 right-1 my-auto h-9 rounded-sm px-2 text-sm font-medium text-primary"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    aria-pressed={showPassword}
                  >
                    {showPassword ? "Hide" : "Show"}
                  </Button>
                </div>
                <p className="text-[13px] leading-5 text-muted-foreground">
                  Use a strong, unique password.
                </p>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            name="acceptTerms"
            control={form.control}
            render={({ field }) => (
              <FormItem className="flex items-start gap-2.5">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    disabled={isSigningUp}
                    className="mt-0.5 size-[18px] rounded-sm"
                  />
                </FormControl>
                <div className="min-w-0 space-y-1">
                  <FormLabel className="block text-[13px] font-normal leading-5 text-foreground">
                    I agree to the{" "}
                    <Link href="/terms-and-conditions" className="underline underline-offset-2">
                      Terms of Service
                    </Link>{" "}
                    and{" "}
                    <Link href="/privacy" className="underline underline-offset-2">
                      Privacy Policy
                    </Link>
                    .
                  </FormLabel>
                  <FormMessage />
                </div>
              </FormItem>
            )}
          />

          <Button type="submit" disabled={isSigningUp} className="h-12 w-full rounded-md text-[15px]">
            {isSigningUp ? (
              <>
                <Loader2 className="size-4 animate-spin" aria-hidden="true" />
                Creating account…
              </>
            ) : (
              "Create account"
            )}
          </Button>
        </form>
      </Form>
    </div>
  );
}
